/**
 * Theme Hook for Chinese Ink-Wash Style Theme
 */

import { useEffect } from 'react';

const THEME_KEY = 'nestmold-theme';
const MODE_KEY = 'nestmold-theme-mode';
const DAY_START = '07:00';
const DAY_END = '19:00';

function parseTime(value: string) {
  const parts = value.split(':');
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  return h * 60 + m;
}

function minutesNow() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

function isDayTime() {
  const now = minutesNow();
  return now >= parseTime(DAY_START) && now < parseTime(DAY_END);
}

export function applyTheme(theme: 'day' | 'night') {
  if (typeof document !== 'undefined') {
    document.body.classList.toggle('cosmos-mode', theme === 'night');
    document.documentElement.setAttribute('data-theme', theme);
  }
}

function getAutoTheme() {
  return isDayTime() ? 'day' : 'night';
}

export function getTheme(): 'day' | 'night' {
  if (typeof window === 'undefined') return 'day';

  const mode = localStorage.getItem(MODE_KEY);
  const stored = localStorage.getItem(THEME_KEY);

  if (mode === 'manual' && stored) {
    return stored as 'day' | 'night';
  }
  return getAutoTheme();
}

function setManual(theme: 'day' | 'night') {
  localStorage.setItem(MODE_KEY, 'manual');
  localStorage.setItem(THEME_KEY, theme);
  applyTheme(theme);
}

export function toggleTheme() {
  const current = document.body.classList.contains('cosmos-mode') ? 'night' : 'day';
  const next: 'day' | 'night' = current === 'night' ? 'day' : 'night';
  setManual(next);
}

export function useTheme() {
  useEffect(() => {
    // Apply initial theme
    applyTheme(getTheme());

    // Update theme class on body when it changes
    const handleStorageChange = () => {
      applyTheme(getTheme());
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return {
    theme: getTheme(),
    isDark: getTheme() === 'night',
    toggleTheme,
    isCosmosMode: typeof document !== 'undefined' && document.body.classList.contains('cosmos-mode'),
  };
}